import React, { useState } from "react";
import "../../styles/sidebar/ProfileSearch.css";

const API = "https://api.anti-bot.ai";

const BOT_COLORS = {
  BOT:        { bg: "rgba(239,68,68,0.12)",  border: "rgba(239,68,68,0.35)",  text: "#ef4444", label: "בוט" },
  LIKELY_BOT: { bg: "rgba(249,115,22,0.12)", border: "rgba(249,115,22,0.35)", text: "#f97316", label: "חשוד" },
  HUMAN:      { bg: "rgba(34,197,94,0.12)",  border: "rgba(34,197,94,0.35)",  text: "#22c55e", label: "אנושי" },
};

function StatBox({ value, label }) {
  return (
    <div className="ps-stat">
      <span className="ps-stat-val">{Number(value || 0).toLocaleString()}</span>
      <span className="ps-stat-label">{label}</span>
    </div>
  );
}

function ScoreBar({ score }) {
  const color = score >= 70 ? "#ef4444" : score >= 40 ? "#f97316" : "#22c55e";
  return (
    <div className="ps-score-bar-wrap">
      <div className="ps-score-bar-track">
        <div
          className="ps-score-bar-fill"
          style={{ width: `${score}%`, background: color }}
        />
      </div>
      <span className="ps-score-bar-num" style={{ color }}>{score}/100</span>
    </div>
  );
}

export default function ProfileSearch() {
  const [input, setInput]     = useState("");
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [error, setError]     = useState(null);

  const handleSearch = async () => {
    const username = input.trim().replace(/^@/, "");
    if (!username) return;

    setLoading(true);
    setProfile(null);
    setError(null);

    try {
      const res  = await fetch(`${API}/api/profile/${username}`);
      const data = await res.json();

      if (!data.success) {
        setError(data.error || "משתמש לא נמצא");
      } else {
        setProfile(data);
      }
    } catch {
      setError("שגיאה בחיבור לשרת");
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const p   = profile?.profile;
  const bot = profile?.bot_analysis;
  const botStyle = bot ? (BOT_COLORS[bot.bot_type] || BOT_COLORS.HUMAN) : null;

  return (
    <div className="ps-root" dir="rtl">

      {/* Header */}
      <div className="ps-header">
        <h1 className="ps-title">חיפוש פרופיל</h1>
        <p className="ps-subtitle">הזן שם משתמש לקבלת מידע מלא מ-X</p>
      </div>

      {/* Search */}
      <div className="ps-search-wrap">
        <div className="ps-search-box">
          <span className="ps-at">@</span>
          <input
            className="ps-input"
            type="text"
            placeholder="שם משתמש..."
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKey}
            autoFocus
          />
          <button
            className="ps-btn"
            onClick={handleSearch}
            disabled={loading || !input.trim()}
          >
            {loading ? <span className="ps-spinner" /> : "חפש"}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="ps-error">❌ {error}</div>
      )}

      {/* Profile Card */}
      {p && (
        <div className="ps-card">

          {/* Source badge */}
          <div className="ps-source-badge">
            {profile.source === "both"    ? "✓ DB + X API" :
             profile.source === "twitter" ? "X API בלבד"   : "DB בלבד"}
          </div>

          {/* Top */}
          <div className="ps-card-top">
            <div className="ps-avatar-wrap">
              {p.profile_image ? (
                <img
                  src={p.profile_image.replace("_normal", "_400x400")}
                  alt={p.name}
                  className="ps-avatar"
                />
              ) : (
                <div className="ps-avatar-placeholder">
                  {(p.name || p.username || "?")[0].toUpperCase()}
                </div>
              )}
              {bot && (
                <div
                  className="ps-bot-badge"
                  style={{ background: botStyle.bg, border: `1px solid ${botStyle.border}`, color: botStyle.text }}
                >
                  {botStyle.label}
                </div>
              )}
            </div>

            <div className="ps-identity">
              <div className="ps-name">{p.name || p.username}</div>
              <div className="ps-username">@{p.username}</div>
              {p.location && <div className="ps-location">📍 {p.location}</div>}
              {p.country_code && <div className="ps-country">🌍 {p.country_code}</div>}
            </div>

            {p.twitter_url && (
              <a
                href={p.twitter_url}
                target="_blank"
                rel="noopener noreferrer"
                className="ps-x-link"
              >
                פתח ב-X ↗
              </a>
            )}
          </div>

          {/* Bio */}
          {p.description && (
            <div className="ps-bio">{p.description}</div>
          )}

          {/* Stats */}
          <div className="ps-stats">
            <StatBox value={p.followers}   label="עוקבים" />
            <StatBox value={p.following}   label="עוקב" />
            <StatBox value={p.tweet_count} label="ציוצים" />
            {p.like_count    != null && <StatBox value={p.like_count}   label="לייקים" />}
            {p.listed_count  != null && <StatBox value={p.listed_count} label="רשימות" />}
            {p.reply_count_in_db != null && p.reply_count_in_db > 0 &&
              <StatBox value={p.reply_count_in_db} label="תגובות בDB" />}
          </div>

          {/* Bot Analysis */}
          {bot && (
            <div className="ps-bot-section" style={{ borderColor: botStyle.border }}>
              <div className="ps-bot-header">
                <span className="ps-bot-title">ניתוח בוט</span>
                <span className="ps-bot-type" style={{ color: botStyle.text }}>
                  {bot.bot_type}
                </span>
              </div>
              <ScoreBar score={bot.bot_score} />
              <div className="ps-bot-meta">
                <span>רמת ביטחון: {Math.round((bot.confidence || 0) * 100)}%</span>
                {bot.analyzed_at && (
                  <span>נותח: {new Date(bot.analyzed_at).toLocaleDateString('he-IL')}</span>
                )}
              </div>
            </div>
          )}

          {/* DB Info */}
          {profile.in_db && (
            <div className="ps-db-info">
              <span className="ps-db-dot" />
              <span>קיים במסד הנתונים</span>
              {p.country_ip && <span>· IP: {p.country_ip}</span>}
              {p.created_at && (
                <span>· הצטרף: {new Date(p.created_at).toLocaleDateString('he-IL')}</span>
              )}
            </div>
          )}

        </div>
      )}

    </div>
  );
}